import React, { useState } from 'react';
import { Grid, List, FolderPlus, FolderInput, Upload, Trash2, CheckSquare, XSquare, ArrowUpDown, Search, X, ArrowLeft, MoreVertical, Check, ChevronRight } from 'lucide-react';
import { useLanguage } from '../../i18n';

// Minimalist "three-object" header (New File Manager ticket): Search bar + sort-
// direction arrows + a three-dot global menu, with a reserved far-right slot for a
// dynamic Red Bin that only appears when items are selected. All the former
// header buttons (New Folder, Upload, view-mode, sort-field, Select) now live
// inside the three-dot menu.
const FileManagerToolbar = ({
    canGoBack,
    onBack,
    viewMode,
    onViewModeChange,
    sortBy,
    onSortByChange,
    sortOrder,
    onSortOrderChange,
    onCreateFolder,
    onUpload,
    onOpenTrash,
    hasSelection,
    selectionCount,
    allSelected,
    onDelete,
    onMove,
    onSelectAll,
    onClearSelection,
    selectionMode,
    onEnterSelectionMode,
    onExitSelectionMode,
    searchQuery,
    onSearchChange,
}) => {
    const { t } = useLanguage();
    const [menuOpen, setMenuOpen] = useState(false);
    const [organizeOpen, setOrganizeOpen] = useState(false);
    const [viewOpen, setViewOpen] = useState(false);

    const closeMenu = () => { setMenuOpen(false); setOrganizeOpen(false); setViewOpen(false); };

    const menuItem = 'w-full flex items-center gap-2.5 px-4 py-2.5 text-left text-sm font-medium text-pai-body hover:bg-white/[0.07] hover:text-white transition-colors';
    const subItem = 'w-full flex items-center gap-2.5 pl-10 pr-4 py-2 text-left text-sm text-pai-secondary hover:bg-white/[0.07] hover:text-white transition-colors';

    return (
        <div className="flex items-center gap-2 px-6 py-4 border-b border-pai-hairline">
            {/* Back arrow — contextual, only inside a subfolder (File Manager Arrow ticket). */}
            {canGoBack && (
                <button
                    onClick={onBack}
                    className="pai-icon-btn w-[42px] h-[42px] border border-pai flex-shrink-0"
                    title={t('fmBackToParent', 'Back to parent folder')}
                    aria-label={t('fmBackToParent', 'Back to parent folder')}
                >
                    <ArrowLeft size={18} />
                </button>
            )}

            {/* Object 1 — Search bar (centered). */}
            <div className="flex-1 max-w-sm mx-auto">
                <div className="pai-input relative flex items-center h-[42px]">
                    <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/45 pointer-events-none" />
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => onSearchChange(e.target.value)}
                        placeholder={t('fmSearchDocumentsPlaceholder', 'Search documents...')}
                        className="w-full h-full bg-transparent pl-10 pr-9 text-[14px] text-white placeholder:text-pai-placeholder outline-none"
                    />
                    {searchQuery && (
                        <button
                            onClick={() => onSearchChange('')}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/45 hover:text-white transition-colors"
                        >
                            <X size={14} />
                        </button>
                    )}
                </div>
            </div>

            {/* Object 2 — Ascending/Descending sort-direction toggle. */}
            <button
                onClick={() => onSortOrderChange(sortOrder === 'asc' ? 'desc' : 'asc')}
                className="pai-icon-btn w-[42px] h-[42px] flex-shrink-0"
                title={sortOrder === 'asc' ? t('fmSortAscending', 'Sort ascending') : t('fmSortDescending', 'Sort descending')}
                aria-label={t('fmToggleSortDirection', 'Toggle sort direction')}
            >
                <ArrowUpDown size={18} className={sortOrder === 'desc' ? 'transform rotate-180' : ''} />
            </button>

            {/* Object 3 — Three-dot global menu (New Folder / Upload / Organize / View / Select). */}
            <div className="relative">
                <button
                    onClick={() => setMenuOpen((v) => !v)}
                    className="pai-icon-btn w-[42px] h-[42px]"
                    title={t('fmMenuTitle', 'Menu')}
                    aria-haspopup="menu"
                    aria-expanded={menuOpen}
                    aria-label={t('fmMenuAriaLabel', 'File manager menu')}
                >
                    <MoreVertical size={18} />
                </button>
                {menuOpen && (
                    <>
                        <div data-testid="fm-menu-backdrop" className="fixed inset-0 z-[60]" onClick={closeMenu} />
                        <div role="menu" className="absolute right-0 mt-1.5 z-[61] w-56 bg-pai-modal border border-pai rounded-card shadow-modal-sm py-1.5 overflow-hidden">
                            <button role="menuitem" className={menuItem} onClick={() => { closeMenu(); onCreateFolder(); }}>
                                <FolderPlus size={16} /> {t('fmNewFolder', 'New folder')}
                            </button>
                            <button role="menuitem" className={menuItem} onClick={() => { closeMenu(); onUpload(); }}>
                                <Upload size={16} /> {t('commonUpload', 'Upload')}
                            </button>
                            {/* Recently Deleted (trash) view (#126). */}
                            <button role="menuitem" data-testid="fm-menu-recently-deleted" className={menuItem} onClick={() => { closeMenu(); onOpenTrash?.(); }}>
                                <Trash2 size={16} /> {t('recentlyDeleted')}
                            </button>

                            {/* Organize ▸ Name / Date / Size */}
                            <button role="menuitem" className={`${menuItem} justify-between`} onClick={() => { setOrganizeOpen((v) => !v); setViewOpen(false); }}>
                                <span className="flex items-center gap-2"><ArrowUpDown size={16} /> {t('fmOrganize', 'Organize')}</span>
                                <ChevronRight size={14} className={organizeOpen ? 'rotate-90 transition-transform' : 'transition-transform'} />
                            </button>
                            {organizeOpen && (
                                <div className="bg-white/[0.03]">
                                    {[['name', t('commonName', 'Name')], ['date', t('commonDate', 'Date')], ['size', t('commonSize', 'Size')]].map(([val, label]) => (
                                        <button key={val} role="menuitem" className={subItem} onClick={() => { closeMenu(); onSortByChange(val); }}>
                                            {sortBy === val ? <Check size={14} className="text-white" /> : <span className="w-[14px]" />}
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* View ▸ Grid / Linear */}
                            <button role="menuitem" className={`${menuItem} justify-between`} onClick={() => { setViewOpen((v) => !v); setOrganizeOpen(false); }}>
                                <span className="flex items-center gap-2"><Grid size={16} /> {t('fmView', 'View')}</span>
                                <ChevronRight size={14} className={viewOpen ? 'rotate-90 transition-transform' : 'transition-transform'} />
                            </button>
                            {viewOpen && (
                                <div className="bg-white/[0.03]">
                                    <button role="menuitem" className={subItem} onClick={() => { closeMenu(); onViewModeChange('grid'); }}>
                                        {viewMode === 'grid' ? <Check size={14} className="text-white" /> : <Grid size={14} />} {t('fmViewGrid', 'Grid')}
                                    </button>
                                    <button role="menuitem" className={subItem} onClick={() => { closeMenu(); onViewModeChange('list'); }}>
                                        {viewMode === 'list' ? <Check size={14} className="text-white" /> : <List size={14} />} {t('fmViewLinear', 'Linear')}
                                    </button>
                                </div>
                            )}

                            <div className="my-1.5 border-t border-pai-hairline" />

                            {/* Select → selection mode. The menu STAYS OPEN so the
                                Select All ⇄ Deselect All toggle + Exit are reachable
                                without reopening it (Select Mission #64). */}
                            {!selectionMode ? (
                                <button role="menuitem" className={menuItem} onClick={() => onEnterSelectionMode?.()}>
                                    <CheckSquare size={16} /> {t('select')}
                                </button>
                            ) : (
                                <>
                                    {/* One toggle: "Select All" until everything is
                                        selected, then it flips to "Deselect All". */}
                                    {allSelected ? (
                                        <button role="menuitem" className={menuItem} onClick={() => onClearSelection()}>
                                            <XSquare size={16} /> {t('fmDeselectAll', 'Deselect All')}
                                        </button>
                                    ) : (
                                        <button role="menuitem" className={menuItem} onClick={() => onSelectAll()}>
                                            <CheckSquare size={16} /> {t('fmSelectAll', 'Select All')}
                                        </button>
                                    )}
                                    <button role="menuitem" className={menuItem} onClick={() => { closeMenu(); onExitSelectionMode?.(); }}>
                                        <X size={16} /> {t('fmExitSelection', 'Exit selection')}
                                    </button>
                                </>
                            )}
                        </div>
                    </>
                )}
            </div>

            {/* Bulk Move (#393) — sits LEFT of the Red Bin, in Select Mode with >=1
                item ticked only (like the bin). The bin keeps its reserved far-right
                slot below, so its position never changes. */}
            {selectionMode && hasSelection && onMove && (
                <button
                    data-testid="fm-bulk-move"
                    onClick={onMove}
                    title={t('fmMoveNSelected', 'Move {n} selected', { n: selectionCount })}
                    aria-label={t('fmMoveNSelected', 'Move {n} selected', { n: selectionCount })}
                    className="relative flex items-center justify-center w-[42px] h-[42px] flex-shrink-0 rounded-ctl border border-pai bg-white/[0.04] text-white/80 hover:bg-white/[0.09] hover:text-white transition-colors"
                >
                    <FolderInput size={18} />
                    <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-white text-black text-[10px] font-bold flex items-center justify-center tnum">
                        {selectionCount}
                    </span>
                </button>
            )}

            {/* Reserved far-right slot for the dynamic Red Bin — shown ONLY in Select
                Mode with ≥1 item selected, so it's hidden during normal browsing and
                can't cause an accidental delete (Red Bin #70). In normal mode, a single
                item is deleted via the right-click context menu's Delete. */}
            <div className="w-[42px] flex-shrink-0 flex items-center justify-center">
                {selectionMode && hasSelection && (
                    <button
                        onClick={onDelete}
                        title={t('fmDeleteNSelected', 'Delete {n} selected', { n: selectionCount })}
                        aria-label={t('fmDeleteNSelected', 'Delete {n} selected', { n: selectionCount })}
                        className="relative flex items-center justify-center w-[42px] h-[42px] rounded-ctl border border-pai-red/50 bg-pai-red/[0.12] text-pai-red hover:bg-pai-red/[0.2] hover:border-pai-red/70 transition-colors"
                    >
                        <Trash2 size={18} />
                        <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-pai-red text-white text-[10px] font-bold flex items-center justify-center tnum">
                            {selectionCount}
                        </span>
                    </button>
                )}
            </div>
        </div>
    );
};

export default FileManagerToolbar;
